// User account and family management types
import type { ApiService } from '../services/api';
import type { UserInfoDto } from './api';
import type { AccountUserSettingDto } from './api/service';

// Family account structure at datacenter level
export interface FamilyAccount {
  familyId: string;
  familyName: string;
  accountTier: 'standard' | 'premium';
  subscriptionStatus: 'active' | 'inactive' | 'trial' | 'expired';
  subscriptionExpiry?: string;
  createdDate: string;
  lastActiveDate: string;
  billingInfo?: {
    email: string;
    paymentMethod?: string;
    lastPaymentDate?: string;
  };
  settings: {
    dataRetentionDays: number;
    allowDataSharing: boolean;
    backupEnabled: boolean;
    syncEnabled: boolean;
  };
}

// Individual app user (parent/caregiver) under family account
export interface AppUser {
  userId: string;
  familyId: string;              // Links to FamilyAccount
  name: string;
  email: string;
  role: 'primary' | 'parent' | 'caregiver' | 'provider';
  
  // Authentication and security
  accessType?: string;          // Access type. admin-console's own sign-in only ever issues
                                 // 25 (system); other values (10=pro, 20=channel, 30=office) can
                                 // still appear on accounts/sessions provisioned by prior forks.
  hashedPassword?: string;       // For local authentication
  authToken?: string;           // Current session token
  refreshToken?: string;        // For token refresh
  lastLoginDate?: string;
  
  // User preferences
  preferences: {
    theme: 'light' | 'dark' | 'auto';
    language: string;
    notifications: {
      enabled: boolean;
      milestoneReminders: boolean;
      concernAlerts: boolean;
      syncUpdates: boolean;
      weeklyReports: boolean;
    };
    privacy: {
      shareDataWithProviders: boolean;
      allowAnalytics: boolean;
      autoSync: boolean;
    };
  };
  
  // App state
  appState: {
    selectedProfileId?: string;   // Currently selected child profile
    lastActiveTab: 'home' | 'milestones' | 'concerns' | 'visits' | 'guidance' | 'actions';
    onboardingCompleted: boolean;
    tutorialSteps: string[];     // Completed tutorial steps
  };
  
  // Permissions for child profiles
  profilePermissions: {
    [profileId: string]: {
      canView: boolean;
      canEdit: boolean;
      canShare: boolean;
      canDelete: boolean;
    };
  };
  
  // Metadata
  createdDate: string;
  lastActiveDate: string;
  deviceInfo?: {
    platform: 'mobile' | 'web';
    deviceId?: string;
    appVersion?: string;
  };
}

// User session state (runtime only)
export interface UserSession {
  user: AppUser | null;
  familyAccount: FamilyAccount | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  // Session management
  sessionStartTime: string;
  lastActivityTime: string;
  sessionTimeoutMinutes: number;
  
  // Network state
  isOnline: boolean;
  lastSyncTime?: string;
  pendingSyncOperations: number;
}

// User state management interface
export interface UserState {
  // Current user session
  session: UserSession;
  
  // Family management
  familyUsers: AppUser[];       // All users in current family
  
  // Authentication actions
  login: (email: string, password: string, signature?: string, accessType?: string, skinType?: string, profileStore?: any, clientId?: string) => Promise<boolean>;
  /**
   * Establishes an authenticated session from an already-issued token pair — the single
   * shared path for "I have a token, make it the session": persists all AUTH_STORAGE_KEYS,
   * updates Zustand session state, syncs authorized users/connections, bootstraps
   * provider_account_get, initializes the active-account store, and replays the boot
   * sequence. `login()` calls this after `authenticate()` succeeds; any other flow that
   * establishes a session from a backend token response (passkey, OTP, etc.) should call
   * this directly instead of re-implementing session establishment.
   */
  applyAuthSession: (params: {
    token: string;
    refreshToken: string;
    email: string;
    userInfo: UserInfoDto;
    accessType: string;
    settings?: AccountUserSettingDto[];
    authorizedUsersData?: { authorizedusers?: any[] } | null;
    connectionsData?: { connections?: any[] } | null;
    runBootSequence?: boolean;
  }) => Promise<{ appUser: AppUser; familyAccount: FamilyAccount }>;
  logout: () => Promise<void>;
  /**
   * Re-hydrates an in-memory session from already-persisted storage (userSession/
   * auth_token/refresh_token/auth_access_type). Makes NO network call and does NOT
   * confirm the token is still valid server-side — it's a local restore, not a
   * refresh. Formerly named `refreshAuth`, renamed to make that distinction explicit
   * after it was found being treated as equivalent to a real backend token refresh.
   */
  restoreSessionFromStorage: () => Promise<boolean>;
  checkStoredAuth: () => Promise<boolean>;
  getStoredItem: (key: string) => Promise<string | null>;
  
  // Profile synchronization
  syncProfilesFromResponse: (
    profileData?: any,
    adminHistoryData?: any,
    accountId?: string,
    profileStore?: any
  ) => Promise<{ success: boolean; message: string; syncResults?: any }>;
  
  // Admin history synchronization
  syncAdminHistoryFromResponse: (
    adminHistoryData?: any,
    accountId?: string
  ) => Promise<{ success: boolean; message: string; syncResults?: any }>;
  
  // Common backend synchronization function
  syncBackendFromResponse: (options: {
    profileData?: any;
    adminHistoryData?: any;
    familyId?: string;
    profileStore?: any;
    contextDescription: string;
  }) => Promise<{ 
    success: boolean; 
    message: string; 
    results: {
      profile?: { success: boolean; message: string; syncResults?: any };
      adminHistory?: { success: boolean; message: string; syncResults?: any };
    };
    errors: string[];
  }>;
  
  // User management
  updateUser: (updates: Partial<AppUser>) => Promise<void>;
  updatePreferences: (preferences: Partial<AppUser['preferences']>) => Promise<void>;
  updateAppState: (appState: Partial<AppUser['appState']>) => Promise<void>;
  
  // Family management
  inviteUser: (email: string, role: AppUser['role']) => Promise<string>;
  removeUser: (userId: string) => Promise<void>;
  updateUserRole: (userId: string, role: AppUser['role']) => Promise<void>;
  
  // Profile permissions
  updateProfilePermissions: (profileId: string, permissions: AppUser['profilePermissions'][string]) => Promise<void>;
  canAccessProfile: (profileId: string) => boolean;
  canEditProfile: (profileId: string) => boolean;
  
  // Session management
  extendSession: () => void;
  isSessionValid: () => boolean;
  checkSessionTimeout: () => boolean;
  
  // Utility getters
  getCurrentUser: () => AppUser | null;
  getFamilyAccount: () => FamilyAccount | null;
  getSelectedProfileId: () => string | null;
  isPremiumUser: () => boolean;
  canInviteUsers: () => boolean;
  
  // Configuration management
  setApiService: (apiService: ApiService) => void;
  getApiService: () => ApiService | null;
  
  // State reset
  reset: () => void;
}

// User creation utilities
export const createDefaultUserPreferences = (): AppUser['preferences'] => ({
  theme: 'auto',
  language: 'en',
  notifications: {
    enabled: true,
    milestoneReminders: true,
    concernAlerts: true,
    syncUpdates: true,
    weeklyReports: true,
  },
  privacy: {
    shareDataWithProviders: false,
    allowAnalytics: true,
    autoSync: true,
  },
});

export const createDefaultAppState = (): AppUser['appState'] => ({
  lastActiveTab: 'home',
  onboardingCompleted: false,
  tutorialSteps: [],
});

export const createDefaultUserSession = (): UserSession => ({
  user: null,
  familyAccount: null,
  isAuthenticated: false,
  isLoading: false,
  sessionStartTime: new Date().toISOString(),
  lastActivityTime: new Date().toISOString(),
  sessionTimeoutMinutes: 30,
  isOnline: true,
  pendingSyncOperations: 0,
});

// Helper functions
export const createAppUser = (userData: {
  name: string;
  email: string;
  familyId: string;
  role: AppUser['role'];
}): Omit<AppUser, 'userId'> => ({
  ...userData,
  preferences: createDefaultUserPreferences(),
  appState: createDefaultAppState(),
  profilePermissions: {},
  createdDate: new Date().toISOString(),
  lastActiveDate: new Date().toISOString(),
});

export const validateUserEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validateUserRole = (role: string): role is AppUser['role'] => {
  return ['primary', 'parent', 'caregiver', 'provider'].includes(role);
};

// Permission helpers
export const getDefaultProfilePermissions = (userRole: AppUser['role']): AppUser['profilePermissions'][string] => {
  switch (userRole) {
    case 'primary':
      return { canView: true, canEdit: true, canShare: true, canDelete: true };
    case 'parent':
      return { canView: true, canEdit: true, canShare: true, canDelete: false };
    case 'caregiver':
      return { canView: true, canEdit: true, canShare: false, canDelete: false };
    case 'provider':
      return { canView: true, canEdit: false, canShare: false, canDelete: false };
    default:
      return { canView: false, canEdit: false, canShare: false, canDelete: false };
  }
};

export const canUserAccessFeature = (user: AppUser | null, feature: 'premium' | 'multipleProfiles' | 'sharing' | 'export'): boolean => {
  if (!user) return false;
  
  // Feature access based on family account tier would be checked here
  // For now, return true for basic features
  switch (feature) {
    case 'premium':
      return user.role === 'primary' || user.role === 'parent';
    case 'multipleProfiles':
      return true;
    case 'sharing':
      return user.role !== 'provider';
    case 'export':
      return user.role === 'primary' || user.role === 'parent';
    default:
      return false;
  }
};